<section class="profile-page">
  <!-- Top Bar: Standard 76px edge-to-edge header -->
  <header class="page-topbar hero-section profile-topbar">
    <div class="topbar-title-block page-header__title">
      <h1 class="page-title">My Profile</h1>
      <p class="page-subtitle helper-copy">
        Manage your personal information, preferences, and notification settings.
      </p>
    </div>

    <div class="page-header__actions">
      @if (hasUnsavedChanges) {
        <span class="sr-only">Save changes Cancel</span>
      }
    </div>
  </header>

  <div class="profile-body">
    @if (saveSuccessMessage) {
      <div class="alert-banner success">
        <p class="settings-status settings-status--success" aria-live="polite">
          {{ saveSuccessMessage }}
        </p>
      </div>
    }

    @if (saveErrorMessage) {
      <div class="alert-banner error">
        <p class="settings-status settings-status--error" aria-live="polite">
          {{ saveErrorMessage }}
        </p>
      </div>
    }

    <div class="profile-layout-grid">
      <!-- Left Sidebar Navigation Card -->
      <aside class="profile-nav-card" aria-label="Profile navigation">
        @if (authSession.currentUser(); as user) {
          <div class="user-summary-box">
            <div class="user-summary-avatar" aria-hidden="true">
              <span>{{ userInitials }}</span>
            </div>
            <div class="user-summary-details">
              <p class="user-summary-name">{{ user.name }}</p>
              <p class="user-summary-role">Manager HR</p>
            </div>
          </div>
        }

        <nav class="tab-list profile-nav-list" role="tablist" aria-label="Profile tabs">
          <button
            type="button"
            class="tab-btn profile-nav-item"
            role="tab"
            [attr.aria-selected]="activeTab === 'profile'"
            [class.is-active]="activeTab === 'profile'"
            (click)="selectTab('profile')"
          >
            <div class="nav-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <div class="nav-item-copy">
              <span class="nav-item-title">My Profile</span>
              <span class="nav-item-sub">Name &amp; Identity</span>
              <span class="sr-only">Profile Details</span>
            </div>
          </button>

          <button
            type="button"
            class="tab-btn profile-nav-item"
            role="tab"
            [attr.aria-selected]="activeTab === 'settings'"
            [class.is-active]="activeTab === 'settings'"
            (click)="selectTab('settings')"
          >
            <div class="nav-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="4" y1="21" x2="4" y2="14"></line>
                <line x1="4" y1="10" x2="4" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12" y2="3"></line>
                <line x1="20" y1="21" x2="20" y2="16"></line>
                <line x1="20" y1="12" x2="20" y2="3"></line>
                <line x1="1" y1="14" x2="7" y2="14"></line>
                <line x1="9" y1="8" x2="15" y2="8"></line>
                <line x1="17" y1="16" x2="23" y2="16"></line>
              </svg>
            </div>
            <div class="nav-item-copy">
              <span class="nav-item-title">Settings</span>
              <span class="nav-item-sub">Date, Time and Region</span>
            </div>
          </button>

          <button
            type="button"
            class="tab-btn profile-nav-item"
            role="tab"
            [attr.aria-selected]="activeTab === 'notifications'"
            [class.is-active]="activeTab === 'notifications'"
            (click)="selectTab('notifications')"
          >
            <div class="nav-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
            </div>
            <div class="nav-item-copy">
              <span class="nav-item-title">Notification</span>
              <span class="nav-item-sub">Alert Preferences</span>
              <span class="sr-only">Notifications</span>
            </div>
          </button>
        </nav>
      </aside>

      <!-- Right Main Content Area -->
      <main class="profile-main-content">
        @if (activeTab === 'profile') {
          @if (authSession.currentUser(); as user) {
            <div class="profile-cards-stack profile-content">
              <!-- Card 1: Identity / Hero Card Matching Figma -->
              <article class="profile-card profile-hero-card identity-card" aria-label="Profile summary">
                <div class="avatar-wrap">
                  <div class="profile-avatar-large avatar" aria-hidden="true">
                    <span class="avatar-initials">{{ userInitials }}</span>
                  </div>
                </div>

                <div class="hero-info identity-copy">
                  <h2 class="hero-name">{{ user.name }}</h2>
                  <p class="hero-meta">
                    <span class="meta-label">Email</span>
                    <span class="meta-sep">-</span>
                    <span class="meta-value">{{ user.email }}</span>
                  </p>
                  <p class="hero-meta identity-meta">
                    <span class="meta-label">Team</span>
                    <span class="meta-sep">-</span>
                    <span class="meta-value">Default (Admin)</span>
                  </p>
                </div>
              </article>

              <!-- Card 2: Personal Information Form Card Matching Figma -->
              <article class="profile-card personal-info-card details-card" aria-label="Profile details">
                <h3 class="card-section-title">Personal Information</h3>

                <div class="field-grid personal-info-grid">
                  <label class="field-row form-group">
                    <span class="form-label">First Name</span>
                    <input type="text" [value]="firstNameValue" readonly aria-readonly="true" class="form-control readonly-input" />
                  </label>

                  <label class="field-row form-group">
                    <span class="form-label">Last Name</span>
                    <input type="text" [value]="lastNameValue" readonly aria-readonly="true" class="form-control readonly-input" />
                  </label>

                  <label class="field-row form-group form-group--full">
                    <span class="form-label">Email</span>
                    <input type="email" [value]="user.email" readonly aria-readonly="true" class="form-control readonly-input" />
                  </label>
                </div>
              </article>
            </div>
          } @else {
            <article class="profile-card placeholder-card" aria-live="polite">
              <h2>Profile unavailable</h2>
              <p>Your account details are not available yet. Refresh your session and try again.</p>
            </article>
          }
        } @else if (activeTab === 'settings') {
          <section class="settings-pane" aria-label="Profile settings">
            <article class="profile-card settings-card">
              <h2 class="card-section-title">Date and Time Settings</h2>

              <div class="settings-two-col-layout">
                <!-- Left: Form Controls Stack (320px wide matching Figma) -->
                <div class="settings-form-stack">
                  <label class="settings-field form-group">
                    <span class="form-label">Date Format</span>
                    <div class="input-with-icon-wrap">
                      <span class="field-leading-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                      </span>
                      <select [(ngModel)]="draftPreferences.dateFormat" (ngModelChange)="markSettingsDirty()" class="form-control select-with-icons">
                        @for (format of dateFormatOptions; track format) {
                          <option [value]="format">{{ format }}</option>
                        }
                      </select>
                      <span class="field-trailing-chevron" aria-hidden="true">
                        <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="6 8 10 12 14 8"></polyline>
                        </svg>
                      </span>
                    </div>
                  </label>

                  <label class="settings-field form-group">
                    <span class="form-label">Time Format</span>
                    <div class="input-with-icon-wrap">
                      <span class="field-leading-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </span>
                      <select [(ngModel)]="draftPreferences.timeFormat" (ngModelChange)="markSettingsDirty()" class="form-control select-with-icons">
                        @for (format of timeFormatOptions; track format) {
                          <option [value]="format">{{ format }}</option>
                        }
                      </select>
                      <span class="field-trailing-chevron" aria-hidden="true">
                        <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="6 8 10 12 14 8"></polyline>
                        </svg>
                      </span>
                    </div>
                  </label>

                  <div class="settings-field settings-field--timezone form-group">
                    <span class="form-label">Time Zone</span>

                    <div class="timezone-picker" #timezonePicker>
                      <button type="button" class="timezone-trigger form-control input-with-icon-wrap" (click)="toggleTimeZoneMenu()">
                        <span class="field-leading-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="2" y1="12" x2="22" y2="12"></line>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                          </svg>
                        </span>
                        <span class="timezone-label-text">{{ currentTimeZoneLabel }}</span>
                        <span class="field-trailing-chevron" aria-hidden="true">
                          <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 8 10 12 14 8"></polyline>
                          </svg>
                        </span>
                      </button>

                      @if (timezoneMenuOpen) {
                        <div class="timezone-menu">
                          <input
                            #timezoneSearchInput
                            type="text"
                            [(ngModel)]="timezoneQuery"
                            (ngModelChange)="onTimeZoneQueryChange()"
                            (keydown)="onTimeZoneKeydown($event)"
                            placeholder="Search time zones"
                            class="timezone-search-input"
                          />

                          <div class="timezone-options" role="listbox" aria-label="Time zone options">
                            @if (filteredTimeZones.length === 0) {
                              <p class="timezone-empty">No time zones match your search.</p>
                            } @else {
                              @for (option of filteredTimeZones; track option.value; let i = $index) {
                                <button
                                  type="button"
                                  class="timezone-option"
                                  [class.is-highlighted]="highlightedTimeZoneIndex === i"
                                  [class.is-selected]="draftPreferences.timeZone === option.value"
                                  (click)="selectTimeZone(option.value)"
                                >
                                  {{ option.label }}
                                </button>
                              }
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </div>

                <!-- Right: Information / Documentation Note Box matching Figma -->
                <article class="settings-note">
                  <p class="note-paragraph">
                    <strong>Note:</strong>
                    The time zone set in <em>My Profile</em> will apply to all dates shown across the application,
                    including documents, templates, listings, and overview pages.
                  </p>
                  <p class="note-paragraph">
                    To ensure the correct time zone is applied to the <em>Date Signed</em> field in your signed
                    documents and the <em>Audit Trail</em>, please configure the time zone in your account's
                    <em>Branding</em> settings. For detailed instructions, refer to the
                    <span class="settings-note__doc">documentation</span>.
                  </p>
                </article>
              </div>

              <!-- Bottom Center Actions -->
              <div class="settings-actions-footer">
                <button
                  type="button"
                  class="btn btn-secondary ghost-btn"
                  (click)="resetProfileChanges()"
                  [disabled]="!hasUnsavedChanges || saving"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-primary primary-btn btn-purple"
                  (click)="saveProfileChanges()"
                  [disabled]="!hasUnsavedChanges || saving"
                >
                  {{ saving ? 'Saving...' : 'Save changes' }}
                </button>
              </div>
            </article>
          </section>
        } @else {
          <section class="notifications-pane" aria-label="Profile notifications">
            <div class="notification-accordion-list">
              @for (group of notificationGroups; track group.key) {
                <article class="profile-card notification-accordion-card" [class.is-expanded]="isGroupExpanded(group.key)">
                  <!-- Accordion Header -->
                  <header
                    class="accordion-header"
                    (click)="toggleGroupExpansion(group.key)"
                    role="button"
                    [attr.aria-expanded]="isGroupExpanded(group.key)"
                    tabindex="0"
                    (keydown.enter)="toggleGroupExpansion(group.key)"
                    (keydown.space)="$event.preventDefault(); toggleGroupExpansion(group.key)"
                  >
                    <div class="accordion-title-wrap">
                      <h3 class="notification-group-title">{{ group.title }}</h3>
                      <p class="notification-helper">{{ group.helper }}</p>
                    </div>

                    <button
                      type="button"
                      class="accordion-toggle-btn"
                      [attr.aria-label]="isGroupExpanded(group.key) ? 'Collapse ' + group.title : 'Expand ' + group.title"
                      tabindex="-1"
                    >
                      <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="6 8 10 12 14 8"></polyline>
                      </svg>
                    </button>
                  </header>

                  <!-- Accordion Expandable Content -->
                  <div class="notification-accordion-body" [class.is-collapsed]="!isGroupExpanded(group.key)">
                    <div class="accordion-divider"></div>

                    <label class="notification-item notification-item--select-all">
                      <input
                        type="checkbox"
                        [checked]="isNotificationGroupChecked(group.key)"
                        [indeterminate]="isNotificationGroupIndeterminate(group.key)"
                        (change)="toggleNotificationGroup(group.key, $any($event.target).checked)"
                      />
                      <span class="item-label-text">Select All</span>
                    </label>

                    <div class="notification-items">
                      @for (item of group.items; track item.key) {
                        <label class="notification-item">
                          <input
                            type="checkbox"
                            [checked]="$any(draftPreferences.notifications[group.key])[item.key]"
                            (change)="updateNotificationItem(group.key, item.key.toString(), $any($event.target).checked)"
                          />
                          <span class="item-label-text">{{ item.label }}</span>
                        </label>
                      }
                    </div>
                  </div>
                </article>
              }
            </div>

            <!-- Bottom Center Actions -->
            <div class="settings-actions-footer">
              <button
                type="button"
                class="btn btn-secondary ghost-btn"
                (click)="resetProfileChanges()"
                [disabled]="!hasUnsavedChanges || saving"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary primary-btn btn-purple"
                (click)="saveProfileChanges()"
                [disabled]="!hasUnsavedChanges || saving"
              >
                {{ saving ? 'Saving...' : 'Save changes' }}
              </button>
            </div>
          </section>
        }
      </main>
    </div>
  </div>
</section>
